import {
   ApiResponse,
   ApisauceConfig,
   ApisauceInstance,
   create as apisauceCreate
} from 'apisauce'
import createAuthRefreshInterceptor from 'axios-auth-refresh'

import type { AxiosRequestConfig } from 'axios'
import { UnAuthorizedError } from '../../errors/UnauthorizedError'

const throwUnauthorizedError = <T, U>(apiResponse: ApiResponse<T, U>) => {
   const isUnauthorizedError = !apiResponse.ok && apiResponse.status === 401

   if (isUnauthorizedError) {
      throw new UnAuthorizedError()
   } else if (!apiResponse.ok) {
      throw apiResponse
   }
}

const apisauceCreateWithAuthInstance = (
   config: ApisauceConfig,
   getAccessToken: () => Promise<string | void>
) => {
   const apisauceInstance = apisauceCreate(config)

   apisauceInstance.addAsyncRequestTransform(request => async () => {
      const accessToken = await getAccessToken()

      if (request.headers && accessToken) {
         request.headers['Authorization'] = `Bearer ${accessToken}`
      }
   })

   return apisauceInstance
}

const create = (
   getAccessToken: () => Promise<string | void>,
   clearAccessTokenCookie: () => void
): ((config: ApisauceConfig) => ApisauceInstance) => {
   const createWithAuth = (config: ApisauceConfig): ApisauceInstance => {
      const apisauceInstance = apisauceCreateWithAuthInstance(
         config,
         getAccessToken
      )

      createAuthRefreshInterceptor(
         apisauceInstance.axiosInstance,
         failedRequest => {
            clearAccessTokenCookie()
            return getAccessToken().then(response => {
               failedRequest.response.config.headers[
                  'Authorization'
               ] = `Bearer ${response}`
               return response
            })
         }
      )

      return {
         ...apisauceInstance,
         get: async <T, U>(
            url: string,
            params?: Record<any, any>,
            axiosConfig?: AxiosRequestConfig
         ) => {
            const apiResponse = await apisauceInstance.get<T, U>(
               url,
               params,
               axiosConfig
            )
            throwUnauthorizedError(apiResponse)

            return apiResponse
         },
         post: async <T, U>(
            url: string,
            data?: any,
            axiosConfig?: AxiosRequestConfig
         ) => {
            const apiResponse = await apisauceInstance.post<T, U>(
               url,
               data,
               axiosConfig
            )
            throwUnauthorizedError(apiResponse)

            return apiResponse
         },
         delete: async <T, U = T>(
            url: string,
            params?: Record<any, any>,
            axiosConfig?: AxiosRequestConfig
         ) => {
            const apiResponse = await apisauceInstance.delete<T, U>(
               url,
               params,
               axiosConfig
            )

            throwUnauthorizedError(apiResponse)

            return apiResponse
         },
         put: async <T, U = T>(
            url: string,
            data?: any,
            axiosConfig?: AxiosRequestConfig
         ) => {
            const apiResponse = await apisauceInstance.put<T, U>(
               url,
               data,
               axiosConfig
            )

            throwUnauthorizedError(apiResponse)

            return apiResponse
         }
      }
   }

   return createWithAuth
}

export default create
